import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  BarLoader,
  Button,
  BUTTON_THEME,
  Icon,
  Input,
  Switch,
  Select,
} from '@st-operations/uikit/src/v2'
import ReactTooltip from 'react-tooltip'

import { getFeatures, getOrgRateLimits, getPublicTaxonomies } from '_services/predactiv-admin'
import { getDestinations } from '_services/destinations'
import { getAPADataSourcesByOrg } from '_services/data-sources-apa'
import { getAudienceFilters, getOmmDestinations } from '_services/enums-apa'
import { getActiveSourcePartners } from '_services/source-partners'
import { isFeatureEnabled } from '../helpers'
import { ConfirmPrompt } from '_components/confirm-prompt'

import './styles.scss'
import CustomSelect from '_components/custom-select'
import CleanroomManager from './cleanroom-manager'
import InvitationsManager from './invitations-manager'
import UsersManager from './users-manager'
import toast from '@st-operations/uikit/lib/toast'
import copyToClipBoard from '../../../lib/copyToClipBoard'

const DATASET_LIMIT_DEFAULT = 100

const RATE_LIMIT_TYPES = [
  { value: 'shared', label: 'Rate Limit' },
  // Uncomment to re-enable per-api-type limits in the admin UI.
  // { value: 'api', label: 'API Rate Limit' },
  // { value: 'mcp', label: 'MCP Rate Limit' },
]

const AUDIENCE_INSIGHTS_CATEGORIES = {
  onlineBehavior: {
    label: 'Online Behavior',
    charts: [
      { value: 'concepts', label: 'Top Topics' },
      { value: 'categories', label: 'Top Categories' },
      { value: 'channel', label: 'Top Channel' },
      { value: 'date', label: 'Interest Over Time' },
      { value: 'domain', label: 'Top Domain' },
      { value: 'asn_org', label: 'ASN Org' },
      { value: 'device_type', label: 'Device Type' },
      { value: 'language', label: 'Language' },
      { value: 'os', label: 'OS' },
    ],
  },
  demographics: {
    label: 'Demographics',
    charts: [
      { value: 'age', label: 'Age' },
      { value: 'genders', label: 'Gender' },
      { value: 'incomes', label: 'Income' },
      { value: 'dwelling_types', label: 'Dwelling Type' },
      { value: 'education', label: 'Education' },
      { value: 'home_owner', label: 'Home Ownership' },
      { value: 'household_incomes', label: 'Household Income' },
      { value: 'marital_statuses', label: 'Marital Status' },
      { value: 'net_worths', label: 'Net Worth' },
      { value: 'occupation_categories', label: 'Occupation' },
      { value: 'political_parties', label: 'Political Party' },
      { value: 'amex_card', label: 'Amex Card Ownership' },
      { value: 'cat_owner', label: 'Cat Ownership' },
      { value: 'children_hh', label: 'Household with Children' },
      { value: 'credit_card', label: 'Credit Card Ownership' },
      { value: 'dog_owner', label: 'Dog Ownership' },
      { value: 'owns_investments', label: 'Investment Ownership' },
      { value: 'pet_owner', label: 'Pet Ownership' },
      { value: 'premium_amex_card', label: 'Premium Amex Card Ownership' },
      { value: 'premium_card', label: 'Premium Credit Card Ownership' },
      { value: 'owns_stocks_and_bonds', label: 'Stocks & Bonds Ownership' },
    ],
  },
  geographics: {
    label: 'Geographics',
    charts: [
      { value: 'geo_iso', label: 'Country' },
      { value: 'geo_subiso', label: 'State' },
      { value: 'city', label: 'City' },
      { value: 'postal_code', label: 'Postal Code' },
    ],
  },
}

// Charts backed by the purchase dataset. They form their own "Purchase
// Behavior" group in the PDP Edit Insights popover and are gated on the
// Purchase-Based Data entitlement below, not on an Audience Insights category
// toggle. See DATASPHERE-4731.
const PURCHASE_BEHAVIOR_CHARTS = [{ value: 'affinity_brands', label: 'Top Brands' }]

const AUDIENCE_INSIGHTS_INITIAL_STATE = {
  onlineBehavior: true,
  demographics: true,
  geographics: true,
  defaultCharts: [],
}

const TAXONOMY_FEATURES_INITIAL_STATE = {
  enabled: false,
  public_taxonomies: [],
}

const getTaxonomyNameFromId = (id, publicTaxonomies) => {
  const taxonomy = publicTaxonomies.find((tax) => tax._id === id)
  return taxonomy ? taxonomy.name : null
}

const Organization = ({ onSave, onCancel, data, env }) => {
  const isNew = !data?._id
  const [orgName, setOrgName] = useState(data.name)
  const [active, setActive] = useState(data.active ?? true)
  const [isDeactivateConfirmOpen, setIsDeactivateConfirmOpen] = useState(false)
  const [features] = useState(data.features)
  const [permissions, setPermissions] = useState(null)
  const [destinations, setDestinations] = useState([])
  const [dataSourcesAPA, setDataSourcesAPA] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [selectedDestinations, setSelectedDestinations] = useState([])
  const [selectedDataSourcesAPA, setSelectedDataSourcesAPA] = useState([])
  const [audienceFeatures, setAudienceFeatures] = useState(data.audienceFeatures ?? {})
  const [datasetLimit, setDatasetLimit] = useState(data?.datasetLimit || DATASET_LIMIT_DEFAULT)
  const [rateLimits, setRateLimits] = useState({})
  const [publicTaxonomies, setPublicTaxonomies] = useState([])
  const [taxonomyFeatures, setTaxonomyFeatures] = useState(TAXONOMY_FEATURES_INITIAL_STATE)
  const [audienceInsights, setAudienceInsights] = useState(
    data?.audienceInsights ??
      (isNew
        ? AUDIENCE_INSIGHTS_INITIAL_STATE
        : { onlineBehavior: false, demographics: false, geographics: false, defaultCharts: [] })
  )
  const [purchaseBasedData, setPurchaseBasedData] = useState(data?.purchaseBasedData ?? false)
  const [disableAudienceBuilder, setDisableAudienceBuilder] = useState(
    data?.disableAudienceBuilder ?? false
  )
  const [hidePendingDataInsights, setHidePendingDataInsights] = useState(
    data?.hidePendingDataInsights ?? false
  )
  const [onlineMediaMeasurement, setOnlineMediaMeasurement] = useState(
    data?.onlineMediaMeasurement ?? false
  )
  const [ommDestinations, setOmmDestinations] = useState(data?.ommDestinations ?? [])
  const [ommDestinationOptions, setOmmDestinationOptions] = useState([])
  const [urlAudience, setUrlAudience] = useState(data?.urlAudience ?? false)
  const [enableRevenueReport, setEnableRevenueReport] = useState(data?.enableRevenueReport ?? false)
  const [allSourcePartners, setAllSourcePartners] = useState([])
  const [selectedSourcePartners, setSelectedSourcePartners] = useState([])
  const [sourcePartnersDefaultMappingId, setSourcePartnersDefaultMappingId] = useState({})

  const fetchPublicTaxonomies = async () => {
    try {
      const publicTaxonomies = await getPublicTaxonomies(env)
      setPublicTaxonomies(publicTaxonomies?.items || [])
    } catch (error) {
      toast.error('Error fetching public taxonomies')
    }
  }
  const fetchSourcePartners = async () => {
    try {
      const response = await getActiveSourcePartners(env)
      setAllSourcePartners(response.items)
    } catch (error) {
      setAllSourcePartners([])
      toast.error('Error while fetching source partners: ' + error.message)
    }
  }

  const fetchDestinations = async () => {
    try {
      const { items } = await getDestinations()
      setDestinations(items)
    } catch (error) {
      setDestinations([])
    }
  }

  const fetchAPADataSourcesByOrg = async () => {
    try {
      const results = await getAPADataSourcesByOrg({ env, organization_id: data._id })
      setDataSourcesAPA(results)
    } catch (error) {
      toast.error('Error fetching data sources')
    }
  }

  const fetchCountryOptions = async () => {
    try {
      const items = await getAudienceFilters()
      setCountryOptions(items)
    } catch (error) {
      setCountryOptions([])
    }
  }

  // The catalogue comes from apa-api's enum, the same one it validates saves against.
  const fetchOmmDestinationOptions = async () => {
    try {
      const items = await getOmmDestinations(env)
      setOmmDestinationOptions(items || [])
    } catch (error) {
      setOmmDestinationOptions([])
      toast.error('Error fetching OMM delivery destinations')
    }
  }

  const fetchRateLimits = async () => {
    try {
      const { rateLimits: fetched } = await getOrgRateLimits({ env, id: data._id })
      const activeTypes = new Set(RATE_LIMIT_TYPES.map((t) => t.value))
      const limits = {}
      ;(fetched || []).forEach(({ apiType, limit }) => {
        if (activeTypes.has(apiType)) limits[apiType] = limit
      })
      setRateLimits(limits)
    } catch (error) {
      toast.error('Error fetching rate limits')
    }
  }

  useEffect(() => {
    if (!data?._id) return
    fetchAPADataSourcesByOrg()
    fetchRateLimits()
  }, [data?._id])

  useEffect(() => {
    fetchDestinations()
    fetchCountryOptions()
    fetchOmmDestinationOptions()
    fetchPublicTaxonomies()
    fetchSourcePartners()
  }, [])

  useEffect(() => {
    if (data?.dataSources?.length) {
      setSelectedDataSourcesAPA(
        data?.dataSources?.map((source) => ({
          value: source._id,
          label: source.name,
        }))
      )
    }

    if (data?.destinations?.length) {
      setSelectedDestinations(
        data?.destinations?.map((destination) => ({
          value: destination._id,
          label: destination.name,
        }))
      )
    }

    if (data?.taxonomyFeatures) {
      setTaxonomyFeatures({
        enabled: data?.taxonomyFeatures?.enabled || false,
        public_taxonomies: data?.taxonomyFeatures?.publicTaxonomies || [],
      })
    }
  }, [data.destinations, data.dataSources, data?.taxonomyFeatures])

  useEffect(() => {
    if (data?.sourcePartners?.length && allSourcePartners.length) {
      const selected = allSourcePartners.filter((partner) =>
        data.sourcePartners.some((sp) => sp._id === partner._id)
      )
      const defaultMappingIds = {}
      for (const partner of data?.sourcePartners || []) {
        if (partner?.defaultMappingId) {
          defaultMappingIds[partner._id] = partner.defaultMappingId
        }
      }
      setSelectedSourcePartners(selected)
      setSourcePartnersDefaultMappingId(defaultMappingIds)
    }
  }, [allSourcePartners, data?.sourcePartners])

  const handleChangePermission = (section, item) => {
    setPermissions((prev) => {
      const newItems = prev[section].map((p) => {
        if (p._id === item._id) p.toggle = !p.toggle
        return p
      })
      return {
        ...prev,
        [section]: newItems,
      }
    })
  }

  const handleChangeSourcePartner = (partners) => {
    setSelectedSourcePartners(partners)
    const defaultMappingIds = {}
    for (const partner of partners) {
      const defaultMappingId = sourcePartnersDefaultMappingId[partner._id]
      if (defaultMappingId) {
        defaultMappingIds[partner._id] = defaultMappingId
      } else {
        defaultMappingIds[partner._id] = partner.mappingIds[0]
      }
    }
    setSourcePartnersDefaultMappingId(defaultMappingIds)
  }

  const handleAudienceInsightsCategoryToggle = (categoryKey, checked) => {
    setAudienceInsights((prev) => {
      const categoryChartValues = AUDIENCE_INSIGHTS_CATEGORIES[categoryKey].charts.map(
        (c) => c.value
      )
      const newDefaults = !checked
        ? prev.defaultCharts.filter((chart) => !categoryChartValues.includes(chart))
        : prev.defaultCharts
      return { ...prev, [categoryKey]: checked, defaultCharts: newDefaults }
    })
  }

  // Purchase-based charts are only offered while the entitlement is on, so
  // turning it off has to drop them from the defaults too — same contract as
  // the Audience Insights category toggles above.
  const handlePurchaseBasedDataToggle = (checked) => {
    setPurchaseBasedData(checked)
    if (checked) return
    const purchaseChartValues = PURCHASE_BEHAVIOR_CHARTS.map((c) => c.value)
    setAudienceInsights((prev) => ({
      ...prev,
      defaultCharts: prev.defaultCharts.filter((chart) => !purchaseChartValues.includes(chart)),
    }))
  }

  // Turning OMM off clears the destinations, since nothing can reach them then.
  const handleOnlineMediaMeasurementToggle = (checked) => {
    setOnlineMediaMeasurement(checked)
    if (!checked) setOmmDestinations([])
  }

  const handleSave = () => {
    if (!isNew) {
      const hasInvalidRateLimit = Object.values(rateLimits).some(
        (limit) => !(parseInt(limit, 10) > 0)
      )
      if (hasInvalidRateLimit) {
        toast.error('Rate limit must be a whole number greater than 0')
        return
      }
    }

    const filteredDestinations = []
    selectedDestinations.forEach(({ value }) => {
      const destination = destinations.find(({ _id }) => _id === value)
      if (destination) filteredDestinations.push(destination)
    })

    const filteredDataSources = []
    selectedDataSourcesAPA.forEach(({ value }) => {
      const dataSource = dataSourcesAPA.find(({ _id }) => _id === value)
      if (dataSource) filteredDataSources.push(dataSource)
    })
    onSave({
      permissions,
      orgName,
      id: data?._id,
      active,
      dataSources: filteredDataSources,
      destinations: filteredDestinations,
      features,
      audienceFeatures,
      audienceInsights,
      purchaseBasedData,
      disableAudienceBuilder,
      hidePendingDataInsights,
      onlineMediaMeasurement,
      ommDestinations,
      urlAudience,
      enableRevenueReport,
      taxonomy_features: taxonomyFeatures,
      datasetLimit: parseInt(datasetLimit, 10) || DATASET_LIMIT_DEFAULT,
      rateLimits: isNew
        ? undefined
        : Object.fromEntries(
            Object.entries(rateLimits).map(([apiType, limit]) => [apiType, parseInt(limit, 10)])
          ),
      selectedSourcePartners: selectedSourcePartners.map((partner) => {
        return {
          _id: partner._id,
          default_mapping_id: sourcePartnersDefaultMappingId[partner._id],
        }
      }),
    })
  }

  useEffect(() => {
    getFeatures(env).then((resp) => {
      const toggledObj = Object.fromEntries(
        data.features?.map((item) => [item._id, item.toggle]) ?? []
      )
      const formatPermissions = (list) =>
        list?.reduce((acc, cur) => {
          cur.toggle = !!toggledObj[cur._id]
          return {
            ...acc,
            [cur.type]: [...(acc[cur.type] ?? []), cur],
          }
        }, {})
      const formatted = formatPermissions(resp.items)
      setPermissions(formatted)
    })
  }, [env, JSON.stringify(data.features)])

  if (!permissions) return <BarLoader />

  // Default Charts offers the charts of every enabled Audience Insights
  // category, plus the purchase-based ones when the org is entitled to them.
  const enabledCategoryCharts = Object.entries(AUDIENCE_INSIGHTS_CATEGORIES)
    .filter(([key]) => audienceInsights[key])
    .flatMap(([, category]) => category.charts)
  const availableCharts = purchaseBasedData
    ? [...enabledCategoryCharts, ...PURCHASE_BEHAVIOR_CHARTS]
    : enabledCategoryCharts
  const allCharts = [
    ...Object.values(AUDIENCE_INSIGHTS_CATEGORIES).flatMap((cat) => cat.charts),
    ...PURCHASE_BEHAVIOR_CHARTS,
  ]

  return (
    <div className="apa-org">
      <div className="apa-org__header">
        <div>
          <button className="apa-org__back-button" onClick={onCancel}>
            <Icon icon="angleleft" type="outline" color="#3d3d3d" height="28px" width="28px" />
          </button>
          <Choose>
            <When condition={isNew}>
              <h2>Create New Organization</h2>
            </When>
            <Otherwise>
              <h2>Edit Organization</h2>
            </Otherwise>
          </Choose>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Switch checked={active} onChange={(e) => { e.persist(); setActive(e.target.checked) }} />
          <span>{active ? 'Active' : 'Deactivated'}</span>
          <Button onClick={onCancel} theme={BUTTON_THEME.SECONDARY}>
            Cancel
          </Button>
          <Button onClick={() => (!active && data.active ? setIsDeactivateConfirmOpen(true) : handleSave())}>Save</Button>
        </div>
      </div>
      <ConfirmPrompt
        title="Deactivate Organization"
        description="This organization will be deactivated. Are you sure you want to continue?"
        okText="Deactivate"
        cancelText="Cancel"
        isOpen={isDeactivateConfirmOpen}
        onIsOpenChange={setIsDeactivateConfirmOpen}
        onOK={handleSave}
      />
      <div className="apa-org__name">
        <Input
          label="Organization Name"
          value={orgName}
          onChange={(value) => setOrgName(value)}
          disabled={!isNew}
        />
      </div>
      {!isNew && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="apa-org__name">
            <Input label="Organization Id" value={data?._id} disabled />
          </div>
          <div
            style={{ cursor: 'pointer', marginTop: '25px' }}
            data-tip
            data-for={`copy-org-id`}
            onClick={() => {
              copyToClipBoard(data?._id)
              toast.success('Copied to clipboard successfully')
            }}
          >
            <Icon color="#02892dff" height="35px" icon="file" type="fill" width="35px" />
            <ReactTooltip id={`copy-org-id`} place="top">
              Copy to clipboard{' '}
            </ReactTooltip>
          </div>
        </div>
      )}

      <If condition={isFeatureEnabled('data_source_config', features)}>
        <div>
          <Select
            name="data_sources_apa"
            label="Data Sources"
            onChange={setSelectedDataSourcesAPA}
            options={dataSourcesAPA?.map((source) => ({
              value: source._id,
              label: source.name,
            }))}
            value={selectedDataSourcesAPA}
            isClearable
            isSearchable
            isMulti
            useReactSelect
          />
        </div>
      </If>
      <If condition={isFeatureEnabled('audience_delivery_destination', features)}>
        <div>
          <Select
            name="destinations"
            label="Destinations"
            onChange={setSelectedDestinations}
            options={destinations?.map((destination) => ({
              value: destination._id,
              label: destination.name,
            }))}
            value={selectedDestinations}
            isClearable={true}
            isSearchable={true}
            isMulti={true}
            useReactSelect
          />
        </div>
      </If>
      <div>
        <div className="apa-org__manage-source-partner">
          <span>Manage Source Partners</span>
          <div className="apa-org__select-source-partner">
            <h4>Select Source Partner</h4>
            <Select
              name="source-partners"
              onChange={(value) => {
                handleChangeSourcePartner(value || [])
              }}
              options={allSourcePartners}
              value={selectedSourcePartners}
              isClearable={true}
              isSearchable={true}
              isMulti={true}
              useReactSelect
            />
          </div>
          <div className="apa-org__source-partner-mapping-id">
            <h4>Select Default Mapping Id</h4>
            <If condition={Object.keys(sourcePartnersDefaultMappingId)?.length > 0}>
              {Object.entries(sourcePartnersDefaultMappingId).map(([key, value]) => {
                return (
                  <div className="apa-org__source-partner-mapping-id__container" key={key}>
                    <div className="apa-org__source-partner-mapping-id__container__label">
                      {allSourcePartners?.find((partner) => partner._id === key).name}
                    </div>
                    <Select
                      name="source-partners"
                      label=""
                      onChange={(value) => {
                        setSourcePartnersDefaultMappingId({
                          ...sourcePartnersDefaultMappingId,
                          [key]: value,
                        })
                      }}
                      options={allSourcePartners?.find((partner) => partner._id === key).mappingIds}
                      value={sourcePartnersDefaultMappingId[key]}
                      isSearchable={false}
                      isMulti={false}
                      useReactSelect
                    />
                  </div>
                )
              })}
            </If>
          </div>
        </div>
      </div>

      <div>
        <div className="apa-org__permission">
          <Input
            label="Dataset Limit"
            value={datasetLimit}
            onChange={(value) => setDatasetLimit(value)}
            type="number"
            min={0}
          />
        </div>
      </div>

      <If condition={!isNew}>
        <div>
          <div className="apa-org__permission flex flex-row gap-2">
            {RATE_LIMIT_TYPES.map(({ value, label }) => (
              <Input
                key={value}
                label={label}
                type="number"
                min={1}
                value={rateLimits[value] ?? ''}
                onChange={(val) => setRateLimits((prev) => ({ ...prev, [value]: val }))}
              />
            ))}
          </div>
          <span className="apa-org__permission-sub">
            Both /v2 endpoints and MCP calls count against this combined requests-per-minute limit.
          </span>
        </div>
      </If>

      <If condition={permissions}>
        <div className="apa-org__manage-permissions">
          <span>Manage Permissions</span>
          <For each="section" of={Object.entries(permissions)}>
            <div key={section} className="apa-org__permissions-block">
              <h4>{section[0]}</h4>
              <For each="item" of={section[1]}>
                <div key={item._id} className="apa-org__permission">
                  <Switch
                    checked={item.toggle}
                    onChange={() => handleChangePermission(section[0], item)}
                  />
                  {item.label}
                </div>
              </For>
              <If condition={section[0] === 'GENERAL'}>
                <div className="apa-org__permission">
                  <Switch
                    checked={disableAudienceBuilder}
                    onChange={(e) => {
                      e.persist()
                      setDisableAudienceBuilder(e.target.checked)
                    }}
                  />
                  Disable Audience Builder
                </div>
                <div className="apa-org__permission">
                  <Switch
                    checked={hidePendingDataInsights}
                    onChange={(e) => {
                      e.persist()
                      setHidePendingDataInsights(e.target.checked)
                    }}
                  />
                  Hide Pending-Data Insight Charts (ASN Org, Device Type,
                  Language, OS)
                </div>
                <div className="apa-org__permission">
                  <Switch
                    checked={urlAudience}
                    onChange={(e) => {
                      e.persist()
                      setUrlAudience(e.target.checked)
                    }}
                  />
                  URL Audience
                </div>
                <div className="apa-org__permission">
                  <Switch
                    checked={enableRevenueReport}
                    onChange={(e) => {
                      e.persist()
                      setEnableRevenueReport(e.target.checked)
                    }}
                  />
                  Revenue Report
                </div>
                <div className="apa-org__permission">
                  <Switch
                    checked={onlineMediaMeasurement}
                    onChange={(e) => {
                      e.persist()
                      handleOnlineMediaMeasurementToggle(e.target.checked)
                    }}
                  />
                  Online Media Measurement
                </div>
                <div>
                  <CustomSelect
                    className="w-full !mb-2"
                    label="OMM Delivery Destinations"
                    isMulti
                    disabled={!onlineMediaMeasurement}
                    placeholder={
                      onlineMediaMeasurement
                        ? 'Select destinations...'
                        : 'Enable Online Media Measurement to select destinations'
                    }
                    value={ommDestinations.map(
                      (value) =>
                        ommDestinationOptions.find((d) => d.value === value) || {
                          value,
                          label: value,
                        }
                    )}
                    options={ommDestinationOptions}
                    onChange={(_1, _2, value) => setOmmDestinations(value)}
                  />
                  <span className="apa-org__permission-sub">
                    Where an OMM model may be delivered. None selected means StatSocial only.
                  </span>
                </div>
              </If>
            </div>
          </For>
          <div className="apa-org__permissions-block">
            <h4>AUDIENCE</h4>
            <div className="apa-org__permission">
              <Switch
                checked={audienceFeatures.hasCountryRestrictions}
                onChange={(e) => {
                  e.persist()
                  setAudienceFeatures((audienceFeatures) => ({
                    ...audienceFeatures,
                    hasCountryRestrictions: e.target.checked,
                  }))
                }}
              />
              Has Country Restrictions
            </div>
          </div>
          <div className="flex flex-row items-center gap-2 w-full">
            <CustomSelect
              className="w-full"
              disabled={!audienceFeatures.hasCountryRestrictions}
              label="Default Countries"
              isMulti
              value={audienceFeatures.defaultCountries}
              options={countryOptions}
              onChange={(_1, _2, value) => {
                setAudienceFeatures((audienceFeatures) => ({
                  ...audienceFeatures,
                  defaultCountries: value,
                }))
              }}
            />
            <CustomSelect
              className="w-full"
              disabled={!audienceFeatures.hasCountryRestrictions}
              label="Allowed Countries"
              isMulti
              value={audienceFeatures.allowedCountries}
              options={countryOptions}
              onChange={(_1, _2, value) => {
                setAudienceFeatures((audienceFeatures) => ({
                  ...audienceFeatures,
                  allowedCountries: value,
                }))
              }}
            />
          </div>
          <div className="apa-org__permissions-block">
            <h4>AUDIENCE INSIGHTS</h4>
            <For each="entry" of={Object.entries(AUDIENCE_INSIGHTS_CATEGORIES)}>
              <div key={entry[0]}>
                <div className="apa-org__permission">
                  <Switch
                    checked={audienceInsights[entry[0]]}
                    onChange={(e) => {
                      e.persist()
                      handleAudienceInsightsCategoryToggle(entry[0], e.target.checked)
                    }}
                  />
                  {entry[1].label}
                </div>
                <span className="apa-org__permission-sub">
                  {entry[1].charts.map((c) => c.label).join(', ')}
                </span>
              </div>
            </For>
          </div>
          <div className="apa-org__permissions-block">
            <h4>PURCHASE-BASED DATA</h4>
            <div className="apa-org__permission">
              <Switch
                checked={purchaseBasedData}
                onChange={(e) => {
                  e.persist()
                  handlePurchaseBasedDataToggle(e.target.checked)
                }}
              />
              Purchase-Based Data
            </div>
            <span className="apa-org__permission-sub">
              {PURCHASE_BEHAVIOR_CHARTS.map((c) => c.label).join(', ')}
            </span>
          </div>
          <div>
            <CustomSelect
              className="w-full !mb-2"
              label="Default Charts"
              isMulti
              disabled={!availableCharts.length}
              placeholder={
                availableCharts.length
                  ? 'Select default charts...'
                  : 'Enable at least one group above to select charts'
              }
              value={audienceInsights.defaultCharts.map((chartValue) => {
                const chart = allCharts.find((c) => c.value === chartValue)
                return chart || { value: chartValue, label: chartValue }
              })}
              options={availableCharts}
              onChange={(_1, _2, value) => {
                setAudienceInsights((prev) => ({
                  ...prev,
                  defaultCharts: value,
                }))
              }}
            />
            <span className="apa-org__permission-sub">
              Only charts from the enabled groups above will be available as options.
            </span>
          </div>
          <div>
            <div className="apa-org__permissions-block">
              <h4>Taxonomy Management</h4>
            </div>
            <div className="flex items-center gap-20 mt-5">
              <div className="flex items-center gap-2">
                Enabled
                <Switch
                  checked={taxonomyFeatures.enabled}
                  onChange={(e) => {
                    e.persist()
                    setTaxonomyFeatures((audienceFeatures) => ({
                      ...audienceFeatures,
                      enabled: e.target.checked,
                    }))
                  }}
                />
              </div>
              <div className="flex items-center gap-2 w-full">
                <span>Public Taxonomies</span>
                <CustomSelect
                  className="w-full my-auto"
                  disabled={!taxonomyFeatures.enabled}
                  label=""
                  isMulti
                  value={taxonomyFeatures.public_taxonomies.map((id) => ({
                    value: id,
                    label: getTaxonomyNameFromId(id, publicTaxonomies) || id,
                  }))}
                  options={publicTaxonomies.map((tax) => ({
                    value: tax._id,
                    label: tax.name,
                  }))}
                  onChange={(_1, _2, value) => {
                    setTaxonomyFeatures((audienceFeatures) => ({
                      ...audienceFeatures,
                      public_taxonomies: value,
                    }))
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </If>
      <If condition={!isNew}>
        <div className="apa-org__section">
          <div className="apa-org__divider" />
          <CleanroomManager organizationId={data._id} env={env} />
        </div>
        <div className="apa-org__section">
          <div className="apa-org__divider" />
          <UsersManager organizationId={data._id} env={env} />
        </div>
        <div className="apa-org__section">
          <InvitationsManager organizationId={data._id} env={env} />
        </div>
      </If>
    </div>
  )
}

Organization.defaultProps = {
  onSave: () => {},
  onCancel: () => {},
}

Organization.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    _id: PropTypes.string.isRequired,
    features: PropTypes.array,
  }),
  env: PropTypes.string.isRequired,
}

export default Organization
